import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../../components/header-components/AdministrationHeader.jsx";
import Tooltip from "../../../components/form-components/Tooltip.jsx";
import ModelStudio from "./ModelStudio";
import ForecastExplorer from "./explorer/ForecastExplorer";
import ScenarioPlanner from "./scenario/ScenarioPlanner";
import FlagReadinessPanel from "../FlagReadinessPanel";
import AutoPromotePanel from "../AutoPromotePanel";
import TermStructurePanel from "./TermStructurePanel";
import CalibrationPanel from "../CalibrationPanel";
import { useFlagReadiness } from "../useFlagReadiness";
import { useModelFlagAB } from "../useModelFlagAB";
import { parseForecastLabParams } from "./deepLink";

const SCOPES = [
  { key: "insights", label: "Insights" },
  { key: "allocation", label: "Allocation" },
];

// Only Model Studio acts on the scope live. Explorer has its own lens toggle
// (which supersedes scope) and Scenario ignores it, so the page-level toggle
// would be dead UI on those tabs — show it only where it does something.
const SCOPED_TABS = new Set(["model-studio"]);
const DEFAULT_TAB = "explorer";

export default function ForecastLabPage() {
  const [searchParams] = useSearchParams();
  // The active tab is driven by the URL (?tab=) so the header tabs (which
  // navigate via links) and the dashboard deep-links share one source of truth.
  const parsed = useMemo(() => parseForecastLabParams(searchParams), [searchParams]);
  const tab = parsed.tab || DEFAULT_TAB;
  const [scope, setScope] = useState(parsed.scope || "insights");

  const { flags, loading: flagLoading, error: flagError, check } = useFlagReadiness();
  const { status: abStatus, flags: abFlags, computedAt: abComputedAt, error: abError, onRun: abOnRun, load: abLoad } = useModelFlagAB();

  // Restore last A/B snapshot on mount (non-blocking)
  useEffect(() => {
    abLoad();
  }, [abLoad]);

  const ab = { status: abStatus, flags: abFlags, computedAt: abComputedAt, error: abError, onRun: abOnRun };

  return (
    <>
      <AdministrationHeader
        title="Forecast Lab"
        className="text-text-3"
        icon={icon({ name: "flask", style: "light" })}
        filters={[]}
        showPeriodSelector={false}
      />
      <div className="px-6 py-4">
        {/* Scope toggle only applies to the model-driven tabs. */}
        {SCOPED_TABS.has(tab) && (
          <div className="mb-4 inline-flex rounded-seg border border-card-bd p-0.5">
            {SCOPES.map((s) => (
              <button
                key={s.key}
                type="button"
                aria-pressed={scope === s.key}
                onClick={() => setScope(s.key)}
                className={`rounded-seg px-3 py-1 text-sm ${
                  scope === s.key
                    ? "bg-primary font-semibold text-white"
                    : "text-text-2 hover:text-text"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div role="tabpanel" id={`panel-${tab}`}>
          {tab === "model-studio" && <ModelStudio scope={scope} />}
          {tab === "explorer" && <ForecastExplorer scope={scope} initial={parsed} />}
          {tab === "scenario" && <ScenarioPlanner scope={scope} />}

          {/* Calibration — coverage + projection-MAPE report and the per-lead band
              term structure. Statistical/diagnostic, grouped away from the working tabs. */}
          {tab === "calibration" && (
            <div className="space-y-6">
              <CalibrationPanel />
              <TermStructurePanel />
            </div>
          )}

          {/* Model Ops — flag-flip readiness and the auto-promotion loop (governance). */}
          {tab === "model-ops" && (
            <div className="space-y-6">
              <FlagReadinessPanel
                flags={flags}
                loading={flagLoading}
                error={flagError}
                onCheck={check}
                ab={ab}
              />
              <AutoPromotePanel />
            </div>
          )}
        </div>
      </div>
      {/* react-tooltip instance for all data-tooltip-id="instantTooltip" anchors
          across the Lab (column-header hints, unit explanations, etc.). */}
      <Tooltip />
    </>
  );
}
